import React, { useEffect, useState } from "react";
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import { SaveBoardStyles, InputTextStyles } from "./SaveBoardForm.styles";
import { useAuth0 } from "@auth0/auth0-react";
import { getToken, nameValidation } from '../../../utils/helpsers';
import Box from "@mui/material/Box";
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import axios from 'axios';
import Alert from '@mui/material/Alert';
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";

/**
 * Returns a form to save groups created by the user.
 * @param {string} type: The board type on lowercase. Example: insights.
 * @param {object} boardData: The current data being displayed by the board.
 * @param {object} customBoard: The data of a previously saved board.
 * @param {string} endpoint: The url to fetch the post.
 * @param {function} onSave: A function to be executed after a board is saved.
 * @returns {JSX.Element}
 */
function SaveGroupForm({ type, boardData,customBoard, endpoint, onSave}) {

  // User session metadata
  const { user } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();

  // The list of board previously created by the current user.
  const[userBoards, setUserBoards] = useState();
  // The action to execute. Options: save_new_board || save_new_version || set_save_mode.
  const[action, setAction] = useState( customBoard ? 'set_save_mode' : 'save_new_board');
  // Status report message.
  const [status, setStatus] = useState();
  const [displayStatus, setDisplayStatus] = useState(false);

  const [inputName, setInputName] = useState("");
  const [getUserBoards, setGetUserBoards] = useState(true)


  // Get all boards created by the current user.
  useEffect(() => {
    if(getUserBoards) {
      axios.get(`${process.env.REACT_APP_API_URL}/custom_boards?created_by=eq.${user.email}`, {
        headers: {
          'ngrok-skip-browser-warning': true
        }
      }).then((res) => {
        setUserBoards(res.data);
        setGetUserBoards(false)
      });
    }
  },[user.email, getUserBoards])

  useEffect(()=>{
    if(action === "save_new_version") {
      setInputName(new Date().toISOString())
    }
  },[action])

  function handleSubmit(e) {
    e.preventDefault();
    // Stores the value of the board or version name.
    let valueName = (inputName).trim();
    // Adding user and date to board version.
    boardData['done_by'] = user.email;
    boardData['created_at'] = new Date().toISOString();

    if(action === "save_new_version") {
      let versions = Object.keys(customBoard.data)

      if(nameValidation({arr: versions, name: valueName})) {
        // The identifier of the custom board loaded.
        let uuid = customBoard.uuid;
        // Adding the new version data to the loaded custom board versions.

        // Cloning original data.
        let rawBody = structuredClone(customBoard);

        rawBody['data'] = {
          ...rawBody.data,
          [valueName]: boardData,
        }

        axios(`${endpoint}?uuid=eq.${uuid}`, {
          method: 'PUT',
          headers: {
            'ngrok-skip-browser-warning': true,
            'Authorization': 'Bearer ' + getToken(),
            'Content-Type': 'application/json',
          },
          data: JSON.stringify(rawBody),
        }).then( () => {
          setDisplayStatus(true);
          setStatus({
            type: 'success',
            message: 'The Board Version was successfully saved'
          });
          setTimeout(() => window.location.reload(), 1000);
        }).catch( err => {
          setDisplayStatus(true);
          setStatus({
            type: 'error',
            message: err.message
          });
        })
      } else {
        setDisplayStatus(true);
        setStatus({
          type: 'error',
          message: 'Version name is already in use'
        });
      }
    }

    // If new board or new copy
    if(action === "save_new_board") {
      let isValidName = nameValidation({arr: Object.values(userBoards), name: valueName, key:'label' })
      // If a is a new copy all version are saved, if new board only the current board data is saved.
      let newBoardData = customBoard ? customBoard.data : {[new Date().toISOString()]: boardData}

      if(isValidName){
        // Board Data. Description is only required for a new groups.
        let label = valueName;
        let description = "";
        let bodyObject = {
          "type": type,
          "label": label,
          "description": description,
          "data": newBoardData,
          "created_by": user.email
        };

        axios.post(endpoint, JSON.stringify(bodyObject), {
          headers: {
            'ngrok-skip-browser-warning': true,
            'Prefer': 'return=representation',
            'Content-Type': 'application/json'
          }
        }).then(response => {
          setDisplayStatus(true);
          setStatus({
            type: 'success',
            message: 'The New Board was successfully saved'
          });
          // Reload page with the uuid of the new board or copy.
          const pathName = location.pathname
          setGetUserBoards(true);
          onSave();
          setTimeout(() => navigate(`${pathName}?shared=${response.data[0].uuid}`), 1000)
        }).catch( err => {
          setDisplayStatus(true);
          setStatus({
            type: 'error',
            message: err.message
          });
        });
      } else {
        setDisplayStatus(true);
        setStatus({
          type: 'error',
          message: 'Board name is already in use'
        });
      }
    }
  }

  return (
      <Box sx={SaveBoardStyles}>
        <div className="saveBoardForm">
          {
            action === 'set_save_mode'
              ? (
                <div className="saveBoardForm__actions">
                  <Button
                    onClick={()=>{ setAction('save_new_board') }}
                    variant="contained"
                    color="purple"
                    type="submit">
                      Create New Copy
                  </Button>
                  <Button
                    onClick={()=>{ setAction('save_new_version') }}
                    variant="contained"
                    color="purple"
                    type="submit">
                      Create New Version
                  </Button>
                </div>
              )
              : (
                <Container component='form' className="saveBoardForm__form" maxWidth="xs" onSubmit={handleSubmit}>
                  <Grid container spacing={2} className={"saveBoard__wrapper"}>
                    <Grid item xs={12} className={"saveBoard__FormItem"}>
                      <TextField sx={InputTextStyles}
                         value={inputName}
                         autoComplete="off"
                         id="inputName"
                         fullWidth
                         variant="outlined"
                         placeholder={ action === "save_new_version" ? "Version Name" : "Board Name"}
                         inputProps={{
                           minLength: 3,
                           pattern: "^[^\\s](?:(?!\\s{2,}).)*[^\\s]$",
                           title: "Board name must start and end with a letter or number, and can't contain consecutive spaces"
                         }}
                         required
                         onChange={ e => setInputName(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6} className={`saveBoard__FormItem doneButton`}>
                      <Button
                        variant="contained"
                        color="purple"
                        type="submit">
                        {
                          !customBoard
                            ? "Save New Board"
                            : (action === "save_new_version" ? "Save new version" : "Save board copy")
                        }
                      </Button>
                    </Grid>
                  </Grid>
                </Container>
                )
          }
        </div>
        <Snackbar
          className="statusMessage"
          open={displayStatus}
          autoHideDuration={6000}
          onClose={() => setDisplayStatus(false)}>
          <Alert onClose={() => setDisplayStatus(false)} variant="filled" severity={`${status?.type ?? 'success'}`}>{status?.message}</Alert>
        </Snackbar>
      </Box>
  );
}

SaveGroupForm.propTypes = {
  type: PropTypes.string.isRequired,
  boardData: PropTypes.object.isRequired,
  customBoard: PropTypes.object,
  endpoint: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired
}

export default SaveGroupForm;

